/**
 * GROWTH — docs/03 §3.8.
 *
 * `GET /leaderboard` is **the product's only poll**: 60 seconds, and only while
 * its screen is visible. Paused on `visibilitychange → hidden`, one immediate
 * refresh on wake. It is a poll because there is no channel for it and nothing
 * about a leaderboard needs to be real-time — which is precisely the argument
 * that has to be made before any second poll is added (Appendix B).
 *
 * `GET /rewards` is refetched on a `notifications` event of type
 * `reward_accrued`, **never on a timer**.
 *
 * One product constraint that lives here rather than in the API: the leaderboard
 * leads with **realised P&L, not win rate** (docs/02 §6.2). On a near-symmetric
 * payoff, a streak or a hit rate presents a coin flip as a game of judgement.
 */

import type { HttpClient, MutationOptions, RequestOptions } from '../client'
import type { OkBody, OpBody, Page, OpQuery } from '../types'

export function growthEndpoints(client: HttpClient) {
  return {
    getLeaderboard(query?: OpQuery<'getLeaderboard'>, options?: RequestOptions) {
      return client.get<Page<OkBody<'getLeaderboard'>>>('/leaderboard', { ...options, query })
    },

    /** Clicking an address on the leaderboard or in the tape; own-profile screen. */
    getUserStats(address: string, options?: RequestOptions) {
      return client.get<OkBody<'getUserStats'>>(
        `/users/${encodeURIComponent(address)}/stats`,
        options,
      )
    },

    /**
     * `/stats` — venue-wide aggregates for 24h or 7d. **Fetched on open and on
     * a period change, never on a timer**: it has no channel, and a poll here
     * would be a fourth network timer, which Appendix B does not list.
     */
    getVenueStats(query?: OpQuery<'getVenueStats'>, options?: RequestOptions) {
      return client.get<OkBody<'getVenueStats'>>('/stats/venue', { ...options, query })
    },

    /**
     * Whether a username is free, ignoring case (`docs/06` §F136). Asked as
     * the user types, debounced, from the choose-a-username window — **never
     * on a timer**, so it is not one of Appendix B's three. An answer about
     * this moment only: the claim is what decides.
     */
    getDisplayNameAvailability(displayName: string, options?: RequestOptions) {
      return client.get<OkBody<'getDisplayNameAvailability'>>(
        `/display-names/${encodeURIComponent(displayName)}`,
        options,
      )
    },

    /**
     * Claims the session's username, once and for good. The identical body
     * again is the same `200`, so a retry is harmless; any other name is
     * `DISPLAY_NAME_ALREADY_SET`.
     */
    claimDisplayName(body: OpBody<'claimDisplayName'>, options?: MutationOptions) {
      return client.post<OkBody<'claimDisplayName'>>('/display-names', body, options)
    },

    listRewards(query?: OpQuery<'listRewards'>, options?: RequestOptions) {
      return client.get<Page<OkBody<'listRewards'>>>('/rewards', { ...options, query })
    },

    listReferrals(query?: OpQuery<'listReferrals'>, options?: RequestOptions) {
      return client.get<Page<OkBody<'listReferrals'>>>('/referrals', { ...options, query })
    },
  }
}

export type GrowthEndpoints = ReturnType<typeof growthEndpoints>
